import React, { useEffect, useState } from "react";
import { Flame, MessageSquare, Star, Sparkles, X, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

/**
 * HomeCommunityPrompt — a prominent, visually striking callout on the home
 * page that nudges visitors to share a trip story or start a thread.
 *
 * Why this exists (iter129): trip_stories has only ~5 real posts and threads
 * are similarly sparse. The community feels dead. The old Wall entry was
 * hidden inside the header — visitors never noticed it. This card lives
 * above-the-fold on the landing page.
 *
 * Dismissal:
 *   Stored in localStorage under `ccu:home-community-prompt:dismissed` with an
 *   ISO timestamp. Re-shows after 7 days so we keep nudging returning visitors.
 */
const DISMISS_KEY = "ccu:home-community-prompt:dismissed";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const IDEAS = [
  { icon: Star, text: "The best pitch to grab (& one to avoid)" },
  { icon: Flame, text: "A campfire tip you learned the hard way" },
  { icon: Sparkles, text: "One thing that made the trip unforgettable" },
];

export default function HomeCommunityPrompt() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (Number.isFinite(ts) && Date.now() - ts < DISMISS_MS) return;
      }
    } catch {
      // localStorage inaccessible (private mode) — fail open
    }
    // Small delay so the card doesn't fight the hero for attention on paint
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const shareTrip = () => {
    if (!user) {
      nav("/login?next=" + encodeURIComponent("/wall?new=1"));
    } else {
      nav("/wall?new=1");
    }
  };

  const startThread = () => {
    if (!user) {
      nav("/login?next=/community");
    } else {
      nav("/community?new=1");
    }
  };

  if (!visible) return null;

  return (
    <section
      className="relative max-w-5xl mx-auto my-10 sm:my-14 rounded-3xl overflow-hidden border-2 border-[#ff4500] shadow-[0_10px_0_0_#0e0e0e] bg-gradient-to-br from-[#fff4ee] via-white to-[#fff9f4]"
      data-testid="home-community-prompt"
      role="region"
      aria-label="Share a trip"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 p-2 rounded-full bg-white/70 hover:bg-white text-[#7a4f00] hover:text-[#0e0e0e] transition-colors z-10"
        aria-label="Dismiss"
        data-testid="home-community-prompt-dismiss"
      >
        <X className="w-4 h-4" strokeWidth={2.25} />
      </button>

      <div className="p-6 sm:p-10 md:p-12">
        <div className="grid md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-8 md:gap-10 items-center">
          {/* LEFT — hero copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ff4500] text-white text-[10px] mono uppercase tracking-widest mb-4 shadow-sm">
              <Flame className="w-3 h-3" strokeWidth={2.5} />
              Campfire community
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-3 text-[#0e0e0e]">
              Camped somewhere brilliant?<br />
              <span className="text-[#ff4500]">Tell the rest of us.</span>
            </h2>
            <p className="text-base sm:text-lg text-[#4a4a4a] leading-relaxed mb-5 max-w-lg">
              Your first-hand tips help other campers pick the perfect spot — and
              turn a directory into a community. One good story a month is enough
              to change someone&rsquo;s weekend.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                type="button"
                onClick={shareTrip}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-[#ff4500] hover:bg-[#e63e00] text-white font-bold text-sm mono uppercase tracking-wider shadow-[0_6px_0_0_#0e0e0e] hover:shadow-[0_3px_0_0_#0e0e0e] hover:translate-y-[3px] transition-all"
                data-testid="home-community-share-trip"
              >
                <Sparkles className="w-4 h-4" strokeWidth={2.25} />
                Share a trip
                <ChevronRight className="w-4 h-4" strokeWidth={2.25} />
              </button>
              <button
                type="button"
                onClick={startThread}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-white border-2 border-[#0e0e0e] hover:bg-[#0e0e0e] hover:text-white text-[#0e0e0e] font-bold text-sm mono uppercase tracking-wider transition-colors"
                data-testid="home-community-start-thread"
              >
                <MessageSquare className="w-4 h-4" strokeWidth={2.25} />
                Start a thread
              </button>
            </div>

            <a
              href="/tips"
              className="inline-flex items-center gap-1.5 text-xs mono uppercase tracking-widest text-[#7a4f00] hover:text-[#ff4500]"
              data-testid="home-community-tips-link"
            >
              <Sparkles className="w-3 h-3" /> Or share a quick tip →
            </a>
          </div>

          {/* RIGHT — idea list, styled as a folded receipt */}
          <div className="bg-white border-2 border-dashed border-[#ffb074] rounded-2xl p-5 sm:p-6 shadow-sm">
            <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-3">
              <Star className="w-3 h-3 fill-[#ff4500]" />
              What makes a good share?
            </div>
            <ul className="space-y-3">
              {IDEAS.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#fff4ee] flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#ff4500]" strokeWidth={2} />
                  </span>
                  <span className="text-sm text-[#0e0e0e] leading-snug pt-1">{text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 pt-4 border-t hairline text-[11px] mono text-[#666] leading-relaxed">
              Even two sentences + one honest photo is enough to help someone
              book with confidence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
